import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  add,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parse,
  startOfToday,
  startOfWeek,
  isBefore,
  startOfDay
} from "date-fns";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/v2/ui/button";

function Calendar({
  className,
  selected,
  onSelect,
  disabled,
  ...props
}) {
  const today = startOfToday();
  const [currentMonth, setCurrentMonth] = React.useState(format(today, "MMM-yyyy"));
  const firstDayCurrentMonth = parse(currentMonth, "MMM-yyyy", new Date());

  const days = eachDayOfInterval({
    start: startOfWeek(firstDayCurrentMonth),
    end: endOfWeek(endOfMonth(firstDayCurrentMonth)),
  });

  const nextMonth = () => {
    const firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
    setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"));
  };

  const prevMonth = () => {
    const firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 });
    setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"));
  };

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  return (
    <div className={cn("p-5 w-full max-w-sm bg-white", className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 relative">
        <button
          onClick={prevMonth}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-9 w-9 p-0 opacity-70 hover:opacity-100 flex items-center justify-center rounded-md border-gray-200"
          )}
          type="button"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <h2 className="text-base font-bold text-gray-900 font-sans">
          {format(firstDayCurrentMonth, "MMMM yyyy")}
        </h2>

        <button
          onClick={nextMonth}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-9 w-9 p-0 opacity-70 hover:opacity-100 flex items-center justify-center rounded-md border-gray-200"
          )}
          type="button"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Day Names Row */}
      <div className="grid grid-cols-7 mb-4">
        {dayNames.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-bold text-gray-400 uppercase tracking-wider"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 gap-y-2">
        {days.map((day, dayIdx) => {
          const isDateDisabled = disabled ? (typeof disabled === 'function' ? disabled(day) : false) : isBefore(startOfDay(day), today);
          const isSelected = selected && isSameDay(day, selected);
          const isCurrentMonth = isSameMonth(day, firstDayCurrentMonth);

          return (
            <div key={day.toString()} className="aspect-square p-1">
              <button
                type="button"
                disabled={isDateDisabled}
                onClick={() => onSelect?.(day)}
                className={cn(
                  "h-11 w-11 flex items-center justify-center rounded-full text-sm sm:text-base transition-all duration-200 mx-auto",
                  !isCurrentMonth && "text-gray-300",
                  isCurrentMonth && !isSelected && "text-gray-700 hover:bg-gray-100",
                  isSelected && "bg-primary text-white font-bold shadow-lg shadow-primary/20",
                  isToday(day) && !isSelected && "border border-primary text-primary font-bold",
                  isDateDisabled && "opacity-20 cursor-not-allowed"
                )}
              >
                {format(day, "d")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
